import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { StatusChip } from "@/components/kcc/Kpi";
import { Download, FileText, Loader2, ShieldCheck } from "lucide-react";

const CADENCE_COLOR = { daily: "info", weekly: "info", monthly: "normal", quarterly: "executive" };

export default function ExecutiveReporting() {
    const [reports, setReports] = useState([]);
    const [generating, setGenerating] = useState(null);
    const [msg, setMsg] = useState("");

    async function refresh() {
        const { data } = await api.get("/reports/catalog");
        setReports(data.reports || []);
    }
    useEffect(() => { refresh(); }, []);

    async function download(r) {
        setGenerating(r.id);
        setMsg("");
        try {
            const res = await fetch(`${API_BASE}/reports/generate/${r.id}`, {
                method: "POST",
                credentials: "include",
                headers: { Authorization: `Bearer ${localStorage.getItem("kcc_access_token") || ""}` },
            });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `KCC360_${r.name.replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setMsg(`Generated: ${r.name}. Delivered as signed, watermarked PDF and audited.`);
            refresh();
        } catch (e) {
            setMsg(`Failed to generate: ${e.message}`);
        } finally {
            setGenerating(null);
        }
    }

    const grouped = reports.reduce((acc, r) => { (acc[r.cadence] = acc[r.cadence] || []).push(r); return acc; }, {});
    const order = ["daily", "weekly", "monthly", "quarterly"];

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Executive Reporting Centre"
                title="Automated Executive & Board Reports"
                description="Blue-chip reports — daily / weekly / monthly / quarterly — with digital signature, watermarking, version control, distribution control and audit trail. One click delivers a signed PDF ready for Board circulation."
            />
            {msg && (
                <div className="kcc-panel p-3 flex items-center gap-3 text-sm" data-testid="report-msg">
                    <ShieldCheck className="w-4 h-4 text-kcc-green" />
                    <div className="text-white">{msg}</div>
                </div>
            )}
            {order.map((c) => {
                const list = grouped[c] || [];
                if (!list.length) return null;
                return (
                    <Panel key={c} title={c.charAt(0).toUpperCase() + c.slice(1) + " Reports"} accent={c === "quarterly" ? "purple" : c === "monthly" ? "cobalt" : "copper"}>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {list.map((r) => (
                                <div key={r.id} className="kcc-surface-elev p-4 flex flex-col gap-3" data-testid={`report-${r.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-start gap-2.5">
                                            <FileText className="w-5 h-5 text-kcc-copper-light mt-0.5" />
                                            <div>
                                                <div className="font-display text-base font-semibold text-white leading-tight">{r.name}</div>
                                                <div className="text-[11px] kcc-text-silver mt-1">{r.area} · {r.recipients} recipients</div>
                                            </div>
                                        </div>
                                        <StatusChip status={CADENCE_COLOR[r.cadence] || "info"}>{r.cadence}</StatusChip>
                                    </div>
                                    <p className="text-xs kcc-text-silver leading-relaxed">{r.description}</p>
                                    <div className="text-[10.5px] kcc-text-dim">Last generated: {r.last_generated.slice(0, 10)} · classification: {r.classification}</div>
                                    <button
                                        onClick={() => download(r)}
                                        disabled={generating === r.id}
                                        className="mt-1 inline-flex items-center justify-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-60 text-white rounded-md px-3 py-2 text-sm transition-colors"
                                        data-testid={`generate-${r.id}`}
                                    >
                                        {generating === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                        Generate signed PDF
                                    </button>
                                </div>
                            ))}
                        </div>
                    </Panel>
                );
            })}
        </div>
    );
}
